import { useCallback, useMemo, useRef } from 'react';

import { useActorRef, useSelector } from '@xstate/react';
import { type ActorRefFrom, type InputFrom } from 'xstate';

import { scrollspyVirtualizedMachine } from './machine';
import { type ScrollspyVirtualizedAction } from './machine/scrollspy-virtualized.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Scrollspy for virtualized lists such as FlashList, where offscreen rows are
 * unmounted and targets cannot be measured.
 *
 * Maps target ids to row indices so a jump can go through `scrollToIndex`, and
 * feeds the list's viewability reports to the machine resolving the active
 * target.
 *
 * @example
 * const listRef = useRef<FlashListRef<Row>>(null);
 *
 * const {
 *   actorRef,
 *   initialScrollIndex,
 *   onViewableItemsChanged,
 *   scrollTo,
 * } = useVirtualizedScrollspy({ rows, getTargetId, listRef, defaultTargetId });
 *
 * // inside a memoized nav component
 * const activeTargetId = useScrollspyActiveTargetId(actorRef);
 *
 * <NavItem isActive={activeTargetId === targetId} onPress={scrollTo} />;
 *
 * <FlashList
 *   ref={listRef}
 *   data={rows}
 *   initialScrollIndex={initialScrollIndex}
 *   onViewableItemsChanged={onViewableItemsChanged}
 * />;
 */
export const useVirtualizedScrollspy = <Row>(params: Params<Row>) => {
  const { rows, getTargetId, listRef, defaultTargetId, onActiveTargetIdChange } = params;

  // ─── Machine ─────────────────────────────────────────────────────────

  const handleActiveTargetChange = useCallback<ScrollspyVirtualizedAction>(
    ({ context }) => {
      if (context.activeTargetId === undefined) return;

      onActiveTargetIdChange?.(context.activeTargetId);
    },
    [onActiveTargetIdChange],
  );

  const findRowIndex = useCallback(
    (targetId: string) => rows.findIndex((row) => getTargetId(row) === targetId),
    [rows, getTargetId],
  );

  // NOTE: The machine reads its input once, while rows can change as a jump
  //       settles, so it looks rows up through the latest `findRowIndex`.
  const findRowIndexRef = useRef(findRowIndex);
  findRowIndexRef.current = findRowIndex;

  const actorRef = useActorRef(
    scrollspyVirtualizedMachine.provide({
      actions: { onActiveTargetChange: handleActiveTargetChange },
    }),
    {
      input: {
        defaultTargetId,
        listRef,
        findTargetIndex: (targetId) => findRowIndexRef.current(targetId),
      },
    },
  );

  const initialTargetId = useSelector(actorRef, (snapshot) => snapshot.context.initialTargetId);

  // ─── Derived ─────────────────────────────────────────────────────────

  const initialScrollIndex = useMemo(() => {
    if (initialTargetId === undefined) return undefined;

    const index = findRowIndex(initialTargetId);

    return index === -1 ? undefined : index;
  }, [findRowIndex, initialTargetId]);

  // ─── Handlers ────────────────────────────────────────────────────────

  const scrollTo = useCallback(
    (targetId: string) => {
      const index = findRowIndex(targetId);

      if (index === -1) return;

      actorRef.send({ type: 'SCROLL_TO_TARGET', targetId });
    },
    [actorRef, findRowIndex],
  );

  const onViewableItemsChanged = useCallback(
    (info: ViewableItemsChangedInfo<Row>) => {
      const visibleTargetIds = info.viewableItems.map((token) => getTargetId(token.item));
      const isScrolledToEnd = checkIfScrolledToEnd({ rows, viewableItems: info.viewableItems });

      // NOTE: Targets at the end of the list can be too short to ever reach the
      //       top, so once the list is scrolled to its end the last one leads.
      const targetIds = isScrolledToEnd ? [...visibleTargetIds].reverse() : visibleTargetIds;

      actorRef.send({ type: 'SET_VISIBLE_TARGETS', targetIds });
    },
    [actorRef, getTargetId, rows],
  );

  // ─────────────────────────────────────────────────────────────────────

  return { actorRef, initialScrollIndex, scrollTo, onViewableItemsChanged };
};

/**
 * Subscribed separately so only the nav re-renders as sections are crossed.
 */
export const useScrollspyActiveTargetId = (actorRef: ScrollspyActorRef) => {
  return useSelector(actorRef, (snapshot) => snapshot.context.activeTargetId);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * The last row is in view and the first one is not. A list that fits on
 * screen whole shows both, and keeps following its first visible row.
 */
function checkIfScrolledToEnd<Row>(params: CheckIfScrolledToEndParams<Row>) {
  const { rows, viewableItems } = params;

  const visibleRows = viewableItems.map(toRow);
  const firstRow = rows.at(0);
  const lastRow = rows.at(-1);

  if (firstRow === undefined || lastRow === undefined) {
    return false;
  }

  return visibleRows.includes(lastRow) && !visibleRows.includes(firstRow);
}

function toRow<Row>(token: ViewToken<Row>) {
  return token.item;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params<Row> = {
  rows: readonly Row[];
  getTargetId: (row: Row) => string;
  listRef: InputFrom<typeof scrollspyVirtualizedMachine>['listRef'];
  defaultTargetId?: string;
  onActiveTargetIdChange?: (targetId: string) => void;
};

type ViewableItemsChangedInfo<Row> = {
  viewableItems: ReadonlyArray<{ item: Row }>;
};

type ViewToken<Row> = ViewableItemsChangedInfo<Row>['viewableItems'][number];

type CheckIfScrolledToEndParams<Row> = Pick<Params<Row>, 'rows'> & ViewableItemsChangedInfo<Row>;

type ScrollspyActorRef = ActorRefFrom<typeof scrollspyVirtualizedMachine>;
